import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL } from '../../config';
import Footer from 'components/Footers/Footer';

const SalaryPolicy = () => {
    const token = localStorage.getItem("token");

    const [salaryPolicy, setSalaryPolicy] = useState({
        fullDayStartTime: '08:00AM',
        fullDayEndTime: '02:00PM',
        halfDayStartTime: '08:00AM',
        halfDayEndTime: '12:00PM',
        earlyLeaveHalfDay: 'halfday',
        earlyLeaveFullDay: 'fullday',
        sundayHoliday: true,
        saturdayHoliday: true,
        monthlyHolidays: [{ month: 1, year: 2025, days: 2 }],
        lateArrivalTime: '09:00AM',
        lateArrivalDeduction: true,
    });
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [monthlyHolidayMonth, setMonthlyHolidayMonth] = useState(1);
    const [monthlyHolidayYear, setMonthlyHolidayYear] = useState(2025);
    const [monthlyHolidayDays, setMonthlyHolidayDays] = useState(2);
    const baseURL = apiURL;

    useEffect(() => {
        fetchSalaryPolicy();
    }, []);

    useEffect(() => {
        if (salaryPolicy.monthlyHolidays && salaryPolicy.monthlyHolidays.length > 0) {
            setMonthlyHolidayMonth(salaryPolicy.monthlyHolidays[0].month);
            setMonthlyHolidayYear(salaryPolicy.monthlyHolidays[0].year);
            setMonthlyHolidayDays(salaryPolicy.monthlyHolidays[0].days);
        }
    }, [salaryPolicy]);

    const fetchSalaryPolicy = async () => {
        try {
            const response = await fetch(`${baseURL}/superadmin/getSalaryPolicy`, {
                method: 'GET', // or any other HTTP method as needed
                headers: {
                    'Authorization': `Bearer ${token}`, // Add token to headers
                    // Any other headers can go here
                },
            });
            const data = await response.json();
            if (data[0]) {
                // Convert time formats
                const formattedData = {
                    ...data[0],
                    fullDayStartTime: formatTimeForInput(data[0].fullDayStartTime),
                    fullDayEndTime: formatTimeForInput(data[0].fullDayEndTime),
                    halfDayStartTime: formatTimeForInput(data[0].halfDayStartTime),
                    halfDayEndTime: formatTimeForInput(data[0].halfDayEndTime),
                    lateArrivalTime: formatTimeForInput(data[0].lateArrivalTime),
                };
                setSalaryPolicy(formattedData);
                setIsEditMode(true);
            } else {
                setIsEditMode(false);
            }
        } catch (error) {
            console.error('Error fetching salary policy:', error);
            toast.error('Error fetching salary policy!');
        }
    };

    // Function to convert "hh:mmAM/PM" to "HH:mm"
    const formatTimeForInput = (time) => {
        if (!time) return '';
        const timeParts = time.match(/(\d+):(\d+)(\w+)/);
        if (!timeParts) return '';

        let hours = parseInt(timeParts[1]);
        const minutes = timeParts[2];
        const ampm = timeParts[3].toLowerCase();

        if (ampm === 'pm' && hours < 12) hours += 12;
        if (ampm === 'am' && hours === 12) hours = 0; // Midnight

        const formattedHours = hours.toString().padStart(2, '0');
        return `${formattedHours}:${minutes}`;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setSalaryPolicy({ ...salaryPolicy, [name]: checked });
        } else {
            setSalaryPolicy({ ...salaryPolicy, [name]: value });
        }
    };

    const handleMonthlyHolidayChange = (e) => {
        const { name, value } = e.target;
        switch (name) {
            case 'monthlyHolidayMonth':
                setMonthlyHolidayMonth(Number(value));
                break;
            case 'monthlyHolidayYear':
                setMonthlyHolidayYear(Number(value));
                break;
            case 'monthlyHolidayDays':
                setMonthlyHolidayDays(Number(value));
                break;
            default:
                break;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Time validation
        if (salaryPolicy.fullDayStartTime >= salaryPolicy.fullDayEndTime) {
            toast.error('Full Day Start Time must be before Full Day End Time.');
            return;
        }

        if (salaryPolicy.halfDayStartTime >= salaryPolicy.halfDayEndTime) {
            toast.error('Half Day Start Time must be before Half Day End Time.');
            return;
        }

        if (salaryPolicy.halfDayEndTime >= salaryPolicy.fullDayEndTime) {
            toast.error('Half Day End Time must be before Full Day End Time.');
            return;
        }

        if (salaryPolicy.lateArrivalTime <= salaryPolicy.fullDayStartTime || salaryPolicy.lateArrivalTime >= salaryPolicy.fullDayEndTime) {
            toast.error('Late Arrival Time must be after Full Day Start Time and before Full Day End Time.');
            return;
        }

        try {
            const url = isEditMode
                ? `${baseURL}/superadmin/salaryPolicy/${salaryPolicy._id}`
                : `${baseURL}/superadmin/salaryPolicy`;
            const method = isEditMode ? 'PUT' : 'POST';

            // Convert time back to "hh:mmAM/PM" format for the API
            const convertTimeForAPI = (time) => {
                if (!time) return '';
                const [hours, minutes] = time.split(':');
                let period = 'AM';
                let formattedHours = parseInt(hours);

                if (formattedHours >= 12) {
                    period = 'PM';
                    if (formattedHours > 12) {
                        formattedHours -= 12;
                    }
                } else if (formattedHours === 0) {
                    formattedHours = 12; // Midnight
                }

                formattedHours = formattedHours.toString().padStart(2, '0');
                return `${formattedHours}:${minutes}${period}`;
            };

            const updatedSalaryPolicy = {
                ...salaryPolicy,
                fullDayStartTime: convertTimeForAPI(salaryPolicy.fullDayStartTime),
                fullDayEndTime: convertTimeForAPI(salaryPolicy.fullDayEndTime),
                halfDayStartTime: convertTimeForAPI(salaryPolicy.halfDayStartTime),
                halfDayEndTime: convertTimeForAPI(salaryPolicy.halfDayEndTime),
                lateArrivalTime: convertTimeForAPI(salaryPolicy.lateArrivalTime),
                monthlyHolidays: [{ month: monthlyHolidayMonth, year: monthlyHolidayYear, days: monthlyHolidayDays }],
            };

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`, // Add token to headers
                },
                body: JSON.stringify(updatedSalaryPolicy),
            });

            if (response.ok) {
                const message = isEditMode ? 'Salary policy updated successfully!' : 'Salary policy added successfully!';
                toast.success(message);
                fetchSalaryPolicy();
                setModalOpen(false);
            } else {
                toast.error('Error saving salary policy!');
            }
        } catch (error) {
            console.error('Error saving salary policy:', error);
            toast.error('Error saving salary policy!');
        }
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
    };

    return (
        <>
            <div
                className="header   d-flex align-items-center"
                style={{
                    minHeight: "600px",
                    backgroundImage:
                        "url(" + require("../../assets/img/theme/alRehman.jpg") + ")",
                    backgroundSize: "cover",
                    backgroundPosition: "center top",
                }}
            >
                <span className="mask bg-gradient-default opacity-8" />
                <Container className="d-flex align-items-center" fluid>
                    <Row>
                        <Col lg="7" md="10">
                            <h1 className="display-4 text-white">Salary Policy Management</h1>
                            <p className="text-white mt-0 mb-5">
                                Manage salary policies for your organization.
                            </p>
                            <Button color="primary" onClick={toggleModal}>
                                {isEditMode ? 'Edit Salary Policy' : 'Add Salary Policy'}
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow">
                            <CardHeader className="border-0">
                                <h3 className="mb-0">Salary & Attendance Policy Details</h3>
                            </CardHeader>
                            <CardBody>
                                {isEditMode ? (
                                    <>
                                        <p><strong>Full Day Start Time:</strong> {salaryPolicy.fullDayStartTime}</p>
                                        <p><strong>Full Day End Time:</strong> {salaryPolicy.fullDayEndTime}</p>
                                        <p><strong>Half Day Start Time:</strong> {salaryPolicy.halfDayStartTime}</p>
                                        <p><strong>Half Day End Time:</strong> {salaryPolicy.halfDayEndTime}</p>
                                        <p><strong>Early Leave Half Day:</strong> {salaryPolicy.earlyLeaveHalfDay}</p>
                                        <p><strong>Early Leave Full Day:</strong> {salaryPolicy.earlyLeaveFullDay}</p>
                                        <p><strong>Sunday Holiday:</strong> {salaryPolicy.sundayHoliday ? 'Yes' : 'No'}</p>
                                        <p><strong>Saturday Holiday:</strong> {salaryPolicy.saturdayHoliday ? 'Yes' : 'No'}</p>
                                        <p><strong>Monthly Holidays:</strong> {`Month: ${salaryPolicy.monthlyHolidays[0].month}, Year: ${salaryPolicy.monthlyHolidays[0].year}, Days: ${salaryPolicy.monthlyHolidays[0].days}`}</p>
                                        <p><strong>Late Arrival Time:</strong> {salaryPolicy.lateArrivalTime}</p>
                                        <p><strong>Late Arrival Deduction:</strong> {salaryPolicy.lateArrivalDeduction ? 'Yes' : 'No'}</p>
                                    </>
                                ) : (
                                    <p>No salary policy defined. Please add one.</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <Modal isOpen={modalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    {isEditMode ? 'Edit Salary Policy' : 'Add Salary Policy'}
                </ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label>Full Day Start Time</Label>
                            <Input
                                type="time"
                                name="fullDayStartTime"
                                value={salaryPolicy.fullDayStartTime}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Full Day End Time</Label>
                            <Input
                                type="time"
                                name="fullDayEndTime"
                                value={salaryPolicy.fullDayEndTime}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Half Day Start Time</Label>
                            <Input
                                type="time"
                                name="halfDayStartTime"
                                value={salaryPolicy.halfDayStartTime}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Half Day End Time</Label>
                            <Input
                                type="time"
                                name="halfDayEndTime"
                                value={salaryPolicy.halfDayEndTime}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Early Leave Half Day</Label>
                            <Input
                                type="select"
                                name="earlyLeaveHalfDay"
                                value={salaryPolicy.earlyLeaveHalfDay}
                                onChange={handleInputChange}
                            >
                                <option value="halfday">Halfday</option>
                                <option value="leave">Leave</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Early Leave Full Day</Label>
                            <Input
                                type="select"
                                name="earlyLeaveFullDay"
                                value={salaryPolicy.earlyLeaveFullDay}
                                onChange={handleInputChange}
                            >
                                <option value="fullday">Fullday</option>
                                <option value="leave">Leave</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label className="d-block">Sunday Holiday</Label>
                            <Input
                                type="checkbox"
                                name="sundayHoliday"
                                checked={salaryPolicy.sundayHoliday}
                                onChange={handleInputChange}
                                style={{ position: 'relative', marginLeft: '0' }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label className="d-block">Saturday Holiday</Label>
                            <Input
                                type="checkbox"
                                name="saturdayHoliday"
                                checked={salaryPolicy.saturdayHoliday}
                                onChange={handleInputChange}
                                style={{ position: 'relative', marginLeft: '0' }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Monthly Holidays</Label>
                            <Row>
                                <Col>
                                    <Label>Month</Label>
                                    <Input
                                        type="number"
                                        name="monthlyHolidayMonth"
                                        value={monthlyHolidayMonth}
                                        onChange={handleMonthlyHolidayChange}
                                    />
                                </Col>
                                <Col>
                                    <Label>Year</Label>
                                    <Input
                                        type="number"
                                        name="monthlyHolidayYear"
                                        value={monthlyHolidayYear}
                                        onChange={handleMonthlyHolidayChange}
                                    />
                                </Col>
                                <Col>
                                    <Label>Days</Label>
                                    <Input
                                        type="number"
                                        name="monthlyHolidayDays"
                                        value={monthlyHolidayDays}
                                        onChange={handleMonthlyHolidayChange}
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                        <FormGroup>
                            <Label>Late Arrival Time</Label>
                            <Input
                                type="time"
                                name="lateArrivalTime"
                                value={salaryPolicy.lateArrivalTime}
                                onChange={handleInputChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label className="d-block">Late Arrival Deduction</Label>
                            <Input
                                type="checkbox"
                                name="lateArrivalDeduction"
                                checked={salaryPolicy.lateArrivalDeduction}
                                onChange={handleInputChange}
                                style={{ position: 'relative', marginLeft: '0' }}
                            />
                        </FormGroup>
                        <Button color="primary" type="submit">
                            Save Salary Policy
                        </Button>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
             <Footer />
        </>
    );
};

export default SalaryPolicy;
